import { CartItem } from './cartService';

export interface OrderItem {
  productId: string;
  name: string;
  price: number;
  imageUrl: string;
  quantity: number;
  selectedSize?: string;
  selectedColor?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  userId: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: string;
  billingAddress: string;
  paymentMethod: 'cod' | 'online';
  paymentStatus: 'pending' | 'completed' | 'failed';
  notes?: string;
  createdAt: string;
}

export interface PlaceOrderRequest {
  shippingAddress: string;
  billingAddress?: string;
  paymentMethod: 'cod' | 'online';
  notes?: string;
  cartItems: CartItem[];
  userId: string;
}

export interface PlaceOrderResponse {
  message: string;
  orderId: string;
  orderNumber: string;
  totalAmount: number;
}

const ORDERS_KEY = 'sf_orders';

const getOrders = (): Order[] => {
  try {
    return JSON.parse(localStorage.getItem(ORDERS_KEY) || '[]');
  } catch {
    return [];
  }
};

const saveOrders = (orders: Order[]) => {
  localStorage.setItem(ORDERS_KEY, JSON.stringify(orders));
};

const generateOrderNumber = () =>
  'SF' + Date.now().toString().slice(-8).toUpperCase();

export const orderService = {
  placeOrder(request: PlaceOrderRequest): PlaceOrderResponse {
    const orders = getOrders();
    const total = request.cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const orderId = Math.random().toString(36).substring(2);
    const orderNumber = generateOrderNumber();

    const order: Order = {
      id: orderId,
      orderNumber,
      userId: request.userId,
      items: request.cartItems.map(i => ({
        productId: i.productId,
        name: i.name,
        price: i.price,
        imageUrl: i.imageUrl,
        quantity: i.quantity,
        selectedSize: i.selectedSize,
        selectedColor: i.selectedColor,
      })),
      totalAmount: total,
      status: 'pending',
      shippingAddress: request.shippingAddress,
      billingAddress: request.billingAddress || request.shippingAddress,
      paymentMethod: request.paymentMethod,
      paymentStatus: 'pending',
      notes: request.notes,
      createdAt: new Date().toISOString(),
    };

    saveOrders([order, ...orders]);
    return { message: 'Order placed successfully', orderId, orderNumber, totalAmount: total };
  },

  getOrdersForUser(userId: string): Order[] {
    return getOrders().filter(o => o.userId === userId);
  },

  getOrderById(orderId: string): Order | undefined {
    return getOrders().find(o => o.id === orderId);
  },

  cancelOrder(orderId: string): Order | undefined {
    const orders = getOrders();
    const order = orders.find(o => o.id === orderId);
    if (!order) return undefined;
    if (!['pending', 'confirmed'].includes(order.status)) return undefined;
    order.status = 'cancelled';
    saveOrders(orders);
    return order;
  },
};
